import type {
  RelatedPublicSegment,
  RelatedPublicSegmentV4,
  RelatedPublicTrip,
  RelatedPublicTripV4
} from "@busbud/core-types/dist/lib/poll-service/api/udm";
import type * as UDM from "@busbud/int-schemas/udm";

export const getSeatById = (
  vehicle: UDM.Vehicle,
  id: string
): UDM.SeatCell | null => getSeatBy("id")(vehicle, id);

const getSeatBy = (key: keyof UDM.SeatCell) => {
  return (vehicle: UDM.Vehicle, value: string): UDM.SeatCell | null => {
    let seat_found: UDM.SeatCell | null = null;
    vehicle.layout?.some(
      floor =>
        "cells" in floor &&
        floor.cells.some(row =>
          row.some(seat => {
            if (isSeatCell(seat) && seat[key] === value) {
              seat_found = seat;
              return true;
            }
            return false;
          })
        )
    );
    return seat_found;
  };
};

export const isSeatCell = (cell: UDM.Cell): cell is UDM.SeatCell =>
  cell.type === "seat";

export const getFloorFromLevel = (
  vehicle_layout: UDM.Vehicle["layout"],
  level: number
) =>
  vehicle_layout && vehicle_layout?.length
    ? (vehicle_layout as UDM.Floor[]).find(floor => floor.level === level)
    : undefined;

export const hasAvailableSeatsForFloor = (floor: UDM.Floor): boolean => {
  return floor.cells.some(row =>
    row.some(cell => isSeatCell(cell) && cell.availability)
  );
};

export const getActiveFloorIndex = (
  already_assigned_seat_ids: (string | null)[],
  vehicle_layout: UDM.Floor[] | null | undefined
) => {
  const first_assigned_seat = already_assigned_seat_ids.find(
    seat_id => seat_id !== null
  );

  const active_floor_index = vehicle_layout?.findIndex(floor => {
    return first_assigned_seat
      ? findActiveSeat(floor, first_assigned_seat)
      : hasAvailableSeatsForFloor(floor);
  });

  if (!active_floor_index) {
    return 0;
  }

  return active_floor_index > -1 ? active_floor_index : 0;
};

export const findActiveSeat = (
  floor: UDM.Floor,
  active_seat_id: string | null
): UDM.SeatCell | undefined => {
  for (const row of floor.cells) {
    const found_active_seat = row.find(
      (cell): cell is UDM.SeatCell =>
        isSeatCell(cell) && cell.id === active_seat_id
    );
    if (found_active_seat) {
      return found_active_seat;
    }
  }
};

/**
 * @param vehicle - It can be undefined because we don't have the vehicle data yet
 * @returns {true} when are have the layout and seat selection is supported
 * @returns {false} when seat selection isn't supported
 * @returns {undefined} when returned at getTrips but data is available at getVehicleLayout.
 */
export const hasSeatSelection = (
  vehicle: UDM.Vehicle | undefined
): boolean | undefined => {
  if (!vehicle || vehicle.layout === null) {
    return false;
  }

  if (vehicle.layout !== undefined) {
    return true;
  }

  return undefined;
};

export const UNKNOWN_LAYOUT = "UNKNOWN_LAYOUT";
export const NO_LAYOUT = "NO_LAYOUT";
export const WITH_LAYOUT = "WITH_LAYOUT";

export const getTripSegmentsByVehicleLayout = (
  layout_to_find: typeof UNKNOWN_LAYOUT | typeof NO_LAYOUT | typeof WITH_LAYOUT,

  segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>,
  trip: RelatedPublicTrip | RelatedPublicTripV4
) => {
  const segments_ids = trip.segment_ids;
  return segments_ids.flatMap<RelatedPublicSegment | RelatedPublicSegmentV4>(
    // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    segment_id => {
      const segment = segments[segment_id];

      if (layout_to_find === WITH_LAYOUT && segment?.vehicle?.layout) {
        return segment;
      }

      if (
        layout_to_find === UNKNOWN_LAYOUT &&
        segment?.vehicle?.layout === undefined
      ) {
        return segment;
      }

      if (layout_to_find === NO_LAYOUT && segment?.vehicle?.layout === null) {
        return segment;
      }

      return [];
    }
  );
};
